export const M10_UNIT_14 = {
  id: "m10-u14-indirect-questions",
  title: "Unit 14: Polite Questions",
  topic: "Indirect Questions + Travel & Service Vocabulary",
  iconType: "map-pin",
  grammarImage: "/m10-u14-indirect-questions.jpg",
  grammarText: "Unit focus: Indirect questions using 'Do you know...?' and 'Could you tell me...?' — polite reformulations of direct questions. Key rule: subject before verb, no inversion, no auxiliary inside. Unlike reported speech, NO backshift — the tense stays the same because the question is still live.",

  sets: [

    // ===================================================================
    // SET 1: DO YOU KNOW + WH- (B1+)
    // ===================================================================
    {
      id: "m10-u14-set1",
      title: "Set 1: Asking Politely at the Airport",
      context: `*Casablanca Airport, 6am — Nadia's journey begins*

Nadia is 31, Moroccan, and meticulous about travel.
She had checked in online, printed her documents, and arrived two hours early.
None of that mattered now — the departures board was blank,
the information desk had a queue of twenty people,
and nobody was sure what was happening.
Nadia decided to ask. Politely.

*Aeropuerto de Casablanca, 6am — Comienza el viaje de Nadia*

Nadia tiene 31 años, es marroquí y es meticulosa con los viajes.
Había hecho el check-in online, imprimido sus documentos y llegado dos horas antes.
Nada de eso importaba ahora — el panel de salidas estaba en blanco,
la mesa de información tenía una cola de veinte personas,
y nadie sabía lo que estaba pasando.
Nadia decidió preguntar. Con educación.`,

      questions: [
        {
          type: "match",
          q: "Match the indirect question rules for 'Do you know + WH-':",
          pairs: [
            { left: "structure", right: "Do you know + WH-word + subject + verb (no inversion!)" },
            { left: "NO inversion", right: "'Where IS the gate?' → 'Do you know where the gate IS?' ✅" },
            { left: "NO backshift", right: "Indirect questions are live — tense stays the same. Not reported speech!" }
          ],
          a: "completed",
          successTip: "DO YOU KNOW + WH-word + subject + verb. Two rules: NO inversion (gate IS, not IS the gate) and NO backshift (IS, not WAS — it's a live question, not reported speech!) 🎯"
        },

        {
          type: "match",
          q: "Match the vocabulary to its meaning:",
          pairs: [
            { left: "flight", right: "vuelo" },
            { left: "delayed", right: "retrasado" },
            { left: "canceled", right: "cancelado" }
          ],
          a: "completed",
          successTip: "FLIGHT (vuelo), DELAYED (retrasado — still happening, just late), CANCELED (cancelado — not happening at all). The difference between delayed and canceled is critical at an airport! ✈️"
        },

        {
          type: "mc",
          q: "Nadia wants to ask: 'Where is gate 12?' — politely. She says:",
          options: [
            "Do you know where gate 12 is?",
            "Do you know where is gate 12?",
            "Do you know where gate 12 was?"
          ],
          a: "Do you know where gate 12 is?",
          tip: "Do you know + where + subject (gate 12) + verb (is). No inversion! No backshift — live question stays present!",
          successTip: "WHERE GATE 12 IS ✅ (subject before verb). WHERE IS GATE 12 ❌ (inversion — direct question word order!). WAS ❌ (backshift — but this is a live question, not reported speech!). 🎯"
        },

        {
          type: "mc",
          q: "Direct question: 'When does the flight to London depart?' → Polite version:",
          options: [
            "Do you know when the flight to London departs?",
            "Do you know when does the flight to London depart?",
            "Do you know when the flight to London departed?"
          ],
          a: "Do you know when the flight to London departs?",
          tip: "When + subject + verb (departs — no does!). No auxiliary inside. No backshift — live question!",
          successTip: "WHEN THE FLIGHT DEPARTS ✅ — no DOES inside indirect questions! 'When does the flight' = direct. 'When the flight departs' = indirect. The auxiliary disappears! ✈️"
        },

        {
          type: "mc",
          q: "Which is the correct indirect version of 'Why is the flight delayed?'",
          options: [
            "Do you know why the flight is delayed?",
            "Do you know why is the flight delayed?",
            "Do you know why the flight was delayed?"
          ],
          a: "Do you know why the flight is delayed?",
          tip: "Why + subject (the flight) + verb (is). No inversion. No backshift — is, not was!",
          successTip: "WHY THE FLIGHT IS DELAYED ✅ — present tense stays present! This is a LIVE question about the current situation. Backshift only happens in reported speech (past context)! ✈️⏰"
        },

        {
          type: "mc",
          q: "Nadia: Direct — 'How long is the delay?' → Indirect:",
          options: [
            "Do you know how long the delay is?",
            "Do you know how long is the delay?",
            "Do you know how long the delay was?"
          ],
          a: "Do you know how long the delay is?",
          tip: "How long + subject (the delay) + verb (is). Live question = present tense stays.",
          successTip: "HOW LONG THE DELAY IS ✅ — subject before verb, present tense maintained. At the airport, asking about a current delay = live question = no backshift! ⏰"
        },

        {
          type: "mc",
          q: "Staff announced something. Nadia missed it. She turns to another passenger: '___ you know ___ the announcement ___?'",
          options: ["Do / what / was", "Could / what / is", "Do / what / is"],
          a: "Do / what / was",
          tip: "Do you know = asking a fellow passenger who may not know. What the announcement WAS = past because the announcement already happened — this IS a real past event!",
          successTip: "Careful! The announcement ALREADY HAPPENED — so WAS is correct here! This is the exception: when the event is genuinely past, the past tense is used. Not backshift — just real past! 🔍"
        },

        {
          type: "mc",
          q: "Nadia needs to find the check-in desk for canceled flights. She asks a staff member: '___ ___ ___ ___ the check-in desk for canceled flights ___?'",
          options: ["Do / you / know / where / is", "Do / you / know / where / are", "Could / you / tell / me / where / is"],
          a: "Do / you / know / where / is",
          tip: "Do you know where the desk IS — subject + verb, no inversion. This is Do you know (uncertain) not Could you tell me.",
          successTip: "DO YOU KNOW WHERE [the check-in desk] IS — the desk is singular so IS. No inversion after WHERE. Do you know = she's asking a fellow passenger who may or may not know! ✈️"
        },

        {
          type: "order",
          q: "Reorder: '¿Sabes cuándo sale el próximo vuelo a Londres?'",
          words: ["Do", "you", "know", "when", "the", "next", "flight", "to", "London", "departs?"],
          a: "Do you know when the next flight to London departs?",
          extra: ["does", "depart", "departed"]
        },

        {
          type: "input",
          q: "Nadia asks a staff member why the departures board is blank: 'Excuse me — ___ ___ ___ why the departures board ___ blank?' (Do you know — present)",
          a: "Do / you / know / is",
          alts: ["do / you / know / is"],
          successTip: "DO YOU KNOW WHY THE DEPARTURES BOARD IS BLANK — live question, present tense, no inversion, no auxiliary. The perfect polite airport question! ✈️✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: COULD YOU TELL ME + WH- (B1+)
    // ===================================================================
    {
      id: "m10-u14-set2",
      title: "Set 2: Asking Someone Who Should Know",
      context: `*Casablanca Airport — the information desk*

After twenty minutes in the queue, Nadia reached the desk.
The staff member looked professional and informed.
This was not a fellow passenger guessing the answer —
this was someone whose job it was to know.
Nadia adjusted her language accordingly.

*Aeropuerto de Casablanca — el mostrador de información*

Tras veinte minutos en la cola, Nadia llegó al mostrador.
El empleado parecía profesional e informado.
No era un pasajero cualquiera adivinando la respuesta —
era alguien cuyo trabajo era saber.
Nadia ajustó su lenguaje en consecuencia.`,

      questions: [
        {
          type: "match",
          q: "Match 'Could you tell me' to its meaning and contrast:",
          pairs: [
            { left: "Could you tell me", right: "¿Podría decirme...? — I believe you have this information" },
            { left: "Do you know", right: "¿Sabe usted...? — You may or may not know" },
            { left: "Both structures", right: "WH-word + subject + verb — same word order rule!" }
          ],
          a: "completed",
          successTip: "COULD YOU TELL ME = I believe you know this (staff, expert). DO YOU KNOW = you might know (fellow passenger, stranger). Same grammar rule inside — but different social meaning outside! 🎭"
        },

        {
          type: "match",
          q: "Match the vocabulary to its meaning:",
          pairs: [
            { left: "luggage", right: "equipaje" },
            { left: "seat", right: "asiento" },
            { left: "cost", right: "costo / costar" }
          ],
          a: "completed",
          successTip: "LUGGAGE (equipaje — always uncountable! not 'a luggage' ❌ but 'a piece of luggage' ✅), SEAT (asiento), COST (noun or verb — 'the cost' / 'how much does it cost?'). 🧳💺💰"
        },

        {
          type: "mc",
          q: "Nadia to the information desk: 'How much does it cost to change my seat?' → Polite version:",
          options: [
            "Could you tell me how much it costs to change my seat?",
            "Could you tell me how much does it cost to change my seat?",
            "Could you tell me how much it cost to change my seat?"
          ],
          a: "Could you tell me how much it costs to change my seat?",
          tip: "Could you tell me + how much + subject (it) + verb (costs — no does!). Live question = present tense.",
          successTip: "HOW MUCH IT COSTS ✅ — no DOES inside! COSTS (present — live question). COST (past — would be backshift, wrong here). The auxiliary disappears inside indirect questions! 💺"
        },

        {
          type: "mc",
          q: "Nadia: 'Where is the luggage carousel for flight CA203?' → Polite to staff:",
          options: [
            "Could you tell me where the luggage carousel for flight CA203 is?",
            "Could you tell me where is the luggage carousel for flight CA203?",
            "Could you tell me where the luggage carousel for flight CA203 was?"
          ],
          a: "Could you tell me where the luggage carousel for flight CA203 is?",
          tip: "Could you tell me + where + subject + verb (is). No inversion. Present tense — the carousel is active now!",
          successTip: "WHERE THE CAROUSEL IS ✅ — subject before verb! IS (not WAS) because the carousel is currently operating. No inversion, no backshift. Perfect! 🧳"
        },

        {
          type: "mc",
          q: "Choose the correct polite version of: 'Which seat has been assigned to me?'",
          options: [
            "Could you tell me which seat has been assigned to me?",
            "Could you tell me which seat was assigned to me?",
            "Could you tell me which seat is assigned to me?"
          ],
          a: "Could you tell me which seat has been assigned to me?",
          tip: "Has been assigned = Present Perfect passive. Live question = same tense. No backshift, no change.",
          successTip: "HAS BEEN ASSIGNED ✅ — the original tense is Present Perfect passive, and it stays! This is a live question about a current assignment — no tense change needed! 💺"
        },

        {
          type: "mc",
          q: "Compare: A passenger asks another passenger vs Nadia asks the desk. Which pair is correct?",
          options: [
            "Passenger: 'Do you know how much the seat upgrade costs?' / Nadia to desk: 'Could you tell me how much the seat upgrade costs?'",
            "Passenger: 'Could you tell me how much the seat upgrade costs?' / Nadia to desk: 'Do you know how much the seat upgrade costs?'",
            "Passenger: 'Do you know how much does the seat upgrade cost?' / Nadia to desk: 'Could you tell me how much does the seat upgrade cost?'"
          ],
          a: "Passenger: 'Do you know how much the seat upgrade costs?' / Nadia to desk: 'Could you tell me how much the seat upgrade costs?'",
          tip: "Do you know = uncertain (fellow passenger). Could you tell me = confident the person knows (desk staff). Same internal grammar!",
          successTip: "DO YOU KNOW (uncertain — fellow passenger) vs COULD YOU TELL ME (confident — desk staff). Both use HOW MUCH + SUBJECT + COSTS (no does!). Register changes, grammar rule stays! 🎭💺"
        },

        {
          type: "mc",
          q: "Nadia: 'How do I get to Terminal 2?' → She asks a staff member:",
          options: [
            "Could you tell me how I get to Terminal 2?",
            "Could you tell me how do I get to Terminal 2?",
            "Could you tell me how I got to Terminal 2?"
          ],
          a: "Could you tell me how I get to Terminal 2?",
          tip: "How + subject (I) + verb (get — no do!). Present tense — live question. No auxiliary inside.",
          successTip: "HOW I GET ✅ — no DO inside indirect questions! The auxiliary DO/DOES disappears when we use Could you tell me. Present stays present — it's a live question! ✈️"
        },

        {
          type: "mc",
          q: "The staff member told Nadia her luggage had already been sent to a different carousel. She asks: '___ you ___ me ___ carousel my luggage ___ sent to?'",
          options: ["Could / tell / which / was", "Do / know / which / is", "Could / tell / where / is"],
          a: "Could / tell / which / was",
          tip: "Could you tell me = staff member should know. Which carousel = WH-. Was sent = past passive — the luggage was ALREADY sent (genuine past!)",
          successTip: "WAS SENT ✅ — because the luggage was already sent to another carousel (genuine past event!). Not backshift — just real past. Could you tell me = staff member knows! 🧳🔍"
        },

        {
          type: "order",
          q: "Reorder: '¿Podría decirme cuánto cuesta añadir una maleta adicional a mi vuelo?'",
          words: ["Could", "you", "tell", "me", "how", "much", "it", "costs", "to", "add", "an", "extra", "piece", "of", "luggage", "to", "my", "flight?"],
          a: "Could you tell me how much it costs to add an extra piece of luggage to my flight?",
          extra: ["does", "cost", "costed"]
        },

        {
          type: "input",
          q: "Nadia's most important question at the desk: '___ ___ ___ ___ where my luggage ___?' (Could you tell me — present)",
          a: "Could / you / tell / me / is",
          alts: ["could / you / tell / me / is"],
          successTip: "COULD YOU TELL ME WHERE MY LUGGAGE IS — the classic airport polite question! No inversion (IS at the end), present tense (live question), Could you tell me (staff member should know). 🧳✅"
        }
      ]
    },

    // ===================================================================
    // SET 3: DO YOU KNOW + IF/WHETHER (B2-)
    // ===================================================================
    {
      id: "m10-u14-set3",
      title: "Set 3: Yes or No — But Politely",
      context: `*Casablanca Airport — the gate area, two hours later*

Nadia had found her gate. The departure board now showed a time —
but the time kept changing.
Around her, other passengers were asking the same questions.
Nobody knew if the flight was still going.
Nobody knew whether anything had been confirmed.
The questions were simple. The politeness made them better.

*Aeropuerto de Casablanca — la zona de embarque, dos horas después*

Nadia había encontrado su puerta. El panel de salidas mostraba una hora —
pero la hora seguía cambiando.
A su alrededor, otros pasajeros hacían las mismas preguntas.
Nadie sabía si el vuelo seguía en pie.
Nadie sabía si algo había sido confirmado.
Las preguntas eran sencillas. La educación las hacía mejores.`,

      questions: [
        {
          type: "match",
          q: "Match the yes/no indirect question rules:",
          pairs: [
            { left: "Do you know + if", right: "informal yes/no — 'Do you know if the flight is delayed?'" },
            { left: "Do you know + whether", right: "formal yes/no — 'Do you know whether the flight has been canceled?'" },
            { left: "NO backshift!", right: "Live question: 'Do you know if it IS delayed?' (not WAS)" }
          ],
          a: "completed",
          successTip: "Yes/No indirect questions use IF or WHETHER — just like reported questions! But NO BACKSHIFT — these are live questions. 'Do you know if it IS delayed?' (present, happening now!) ✈️"
        },

        {
          type: "mc",
          q: "Direct: 'Is the flight to London delayed?' → Polite version:",
          options: [
            "Do you know if the flight to London is delayed?",
            "Do you know if the flight to London was delayed?",
            "Do you know if is the flight to London delayed?"
          ],
          a: "Do you know if the flight to London is delayed?",
          tip: "If + subject + verb (is — no inversion!). Present tense — live question about current status.",
          successTip: "IF THE FLIGHT IS DELAYED ✅ — IS (present, live!) not WAS (past/reported). No inversion after IF. This is about the CURRENT status of the flight! ✈️⏰"
        },

        {
          type: "mc",
          q: "Direct: 'Has the flight been canceled?' → Polite version:",
          options: [
            "Do you know whether the flight has been canceled?",
            "Do you know whether the flight was canceled?",
            "Do you know whether has the flight been canceled?"
          ],
          a: "Do you know whether the flight has been canceled?",
          tip: "Whether = slightly more formal. Has been canceled = Present Perfect — live question, same tense!",
          successTip: "WHETHER THE FLIGHT HAS BEEN CANCELED ✅ — Present Perfect stays Present Perfect (live question!). No inversion after WHETHER. HAS BEEN (not WAS) because it's a current status question! ✈️"
        },

        {
          type: "mc",
          q: "A passenger asks Nadia: 'Do you know ___ the delay ___ more than two hours?'",
          options: ["if / is", "if / was", "whether / were"],
          a: "if / is",
          tip: "If = informal fellow passenger. Is = present — the delay is happening right now!",
          successTip: "IF + IS ✅ — present tense because the delay is CURRENT. Between fellow passengers = IF (informal). The situation is live — no backshift! ⏰"
        },

        {
          type: "mc",
          q: "Compare these two questions. Which one is correct and why? A: 'Do you know if the flight is delayed?' / B: 'She asked if the flight was delayed.'",
          options: [
            "Both correct — A is a live indirect question (no backshift). B is reported speech (backshift applies).",
            "Only A is correct — reported speech never uses if.",
            "Only B is correct — all questions need backshift."
          ],
          a: "Both correct — A is a live indirect question (no backshift). B is reported speech (backshift applies).",
          tip: "This is the key distinction! Indirect question = live = no backshift. Reported speech = past context = backshift.",
          successTip: "THE KEY CONTRAST OF THE UNIT! Indirect question (live) = IS. Reported speech (past) = WAS. Same if, same structure — but different time context means different tense! 🔑"
        },

        {
          type: "mc",
          q: "Nadia wants to know if her connecting flight is still running. She asks staff:",
          options: [
            "Do you know whether my connecting flight is still operating?",
            "Do you know whether my connecting flight was still operating?",
            "Do you know whether is my connecting flight still operating?"
          ],
          a: "Do you know whether my connecting flight is still operating?",
          tip: "Whether = slightly formal (staff). Is still operating = present — live question. No inversion!",
          successTip: "WHETHER + MY FLIGHT + IS STILL OPERATING ✅ — no inversion, present tense (live!), whether (slightly formal for staff). Three correct decisions! ✈️"
        },

        {
          type: "mc",
          q: "Another passenger heard an announcement. Nadia asks them: '___ you know ___ the gate ___ changed?'",
          options: ["Do / if / has", "Could / whether / is", "Do / whether / was"],
          a: "Do / if / has",
          tip: "Do you know = fellow passenger (uncertain). If = informal. Has changed = Present Perfect — very recent change!",
          successTip: "DO YOU KNOW (fellow passenger — may not know!) + IF (informal) + HAS CHANGED (Present Perfect — just happened!). Three decisions matching the context! ✈️🔍"
        },

        {
          type: "mc",
          q: "Nadia asks a staff member directly about the departure status: '___ ___ ___ me whether the flight ___ still ___ to depart on time?'",
          options: ["Could / you / tell / is / scheduled", "Do / you / know / was / scheduled", "Could / you / tell / has / scheduled"],
          a: "Could / you / tell / is / scheduled",
          tip: "Could you tell me = staff member (should know). Whether = formal. Is scheduled = present — live status question.",
          successTip: "COULD YOU TELL ME (staff — should know!) + WHETHER (formal) + IS SCHEDULED (present — current schedule!). The most formal version of the question, perfectly formed! ✈️"
        },

        {
          type: "order",
          q: "Reorder: '¿Sabe si el vuelo a Londres ha sido cancelado o simplemente retrasado?'",
          words: ["Do", "you", "know", "whether", "the", "flight", "to", "London", "has", "been", "canceled", "or", "simply", "delayed?"],
          a: "Do you know whether the flight to London has been canceled or simply delayed?",
          extra: ["if", "was", "is"]
        },

        {
          type: "input",
          q: "Gate announcement was unclear. Nadia asks a nearby passenger: '___ ___ ___ ___ the flight ___ delayed or canceled?' (Do you know + whether — present perfect)",
          a: "Do / you / know / whether / has been",
          alts: ["do / you / know / whether / has been"],
          successTip: "DO YOU KNOW WHETHER THE FLIGHT HAS BEEN DELAYED OR CANCELED — whether...or for alternatives (no if...or!), Present Perfect for current status, no backshift. Perfect polite airport question! ✈️✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: COULD YOU TELL ME + IF/WHETHER (B2-)
    // ===================================================================
    {
      id: "m10-u14-set4",
      title: "Set 4: Formal Yes/No Questions",
      context: `*London — Nadia finally arrives. Hotel check-in and restaurant.*

The flight had eventually departed — four hours late.
Nadia had landed in London, collected her luggage (eventually),
and was now at her hotel.
Tonight she needed a table at the hotel restaurant
for a dinner with her conference colleagues.
She had questions. Formal ones.

*Londres — Nadia llega finalmente. Check-in del hotel y restaurante.*

El vuelo había salido finalmente — con cuatro horas de retraso.
Nadia había aterrizado en Londres, recogido su equipaje (eventualmente),
y ahora estaba en su hotel.
Esta noche necesitaba una mesa en el restaurante del hotel
para cenar con sus colegas de conferencia.
Tenía preguntas. Formales.`,

      questions: [
        {
          type: "match",
          q: "Match Could you tell me + if/whether to its contexts:",
          pairs: [
            { left: "Could you tell me if...", right: "formal yes/no — slightly less formal than whether" },
            { left: "Could you tell me whether...", right: "most formal yes/no — preferred in writing and professional speech" },
            { left: "Could you tell me whether...or", right: "reporting a formal choice between two options" }
          ],
          a: "completed",
          successTip: "COULD YOU TELL ME IF = formal but natural in speech. COULD YOU TELL ME WHETHER = most formal, preferred in writing. COULD YOU TELL ME WHETHER...OR = formal alternative question. All with subject + verb inside! 🎭"
        },

        {
          type: "mc",
          q: "Nadia to hotel reception: 'Is this room available tonight?' → Polite formal version:",
          options: [
            "Could you tell me whether this room is available tonight?",
            "Could you tell me whether this room was available tonight?",
            "Could you tell me whether is this room available tonight?"
          ],
          a: "Could you tell me whether this room is available tonight?",
          tip: "Could you tell me + whether + subject + verb (is). No inversion. Present — live availability question!",
          successTip: "WHETHER THIS ROOM IS AVAILABLE ✅ — IS (present, live!) not WAS. No inversion. Could you tell me (receptionist should know!). The formal hotel register! 🏨"
        },

        {
          type: "mc",
          q: "Nadia to the restaurant: 'Can I reserve a table for eight people?' → Polite version:",
          options: [
            "Could you tell me if I can reserve a table for eight people?",
            "Could you tell me if I could reserve a table for eight people?",
            "Could you tell me whether can I reserve a table for eight people?"
          ],
          a: "Could you tell me if I can reserve a table for eight people?",
          tip: "If = fine in speech. Can = present ability — live question. No inversion after if!",
          successTip: "IF I CAN RESERVE ✅ — CAN (present — 'is it possible right now?'). No inversion after IF. Reserve a table = the vocabulary collocation in context! 🍽️"
        },

        {
          type: "mc",
          q: "Nadia: 'Is the restaurant on the ground floor or the first floor?' → Polite to concierge:",
          options: [
            "Could you tell me whether the restaurant is on the ground floor or the first floor?",
            "Could you tell me if the restaurant is on the ground floor or the first floor?",
            "Could you tell me whether is the restaurant on the ground floor or first floor?"
          ],
          a: "Could you tell me whether the restaurant is on the ground floor or the first floor?",
          tip: "Two alternatives = whether...or (not if...or). Is = present. Subject before verb — no inversion!",
          successTip: "WHETHER...OR (alternatives = whether required!) + IS (present, live) + no inversion. The formal alternative question! 🍽️🔄"
        },

        {
          type: "mc",
          q: "Choose the most formal version for a written hotel request:",
          options: [
            "Could you tell me whether a table has been reserved in the name of Nadia Benali?",
            "Do you know if a table has been reserved for Nadia Benali?",
            "Could you tell me if a table has been reserved for Nadia Benali?"
          ],
          a: "Could you tell me whether a table has been reserved in the name of Nadia Benali?",
          tip: "Written request = most formal = Could you tell me + whether. Has been reserved = Present Perfect passive — live question.",
          successTip: "COULD YOU TELL ME WHETHER (most formal combination!) + HAS BEEN RESERVED (Present Perfect passive — current reservation status). Reserve a table collocation! 🍽️📋"
        },

        {
          type: "mc",
          q: "Nadia checks her bill. She asks the receptionist: '___ ___ ___ me ___ the cost ___ luggage storage ___  included in the room rate?'",
          options: ["Could / you / tell / whether / of / is", "Do / you / know / if / for / is", "Could / you / tell / whether / of / was"],
          a: "Could / you / tell / whether / of / is",
          tip: "Could you tell me (receptionist knows) + whether (formal) + the cost of luggage storage + is (present — live billing question).",
          successTip: "COULD YOU TELL ME WHETHER THE COST OF [luggage storage] IS INCLUDED ✅ — IS (live billing question, not past!). LUGGAGE + COST vocabulary combined! 🧳💰"
        },

        {
          type: "mc",
          q: "Nadia realises her colleague already checked in yesterday. She asks about his room: '___ you know ___ his seat at tonight's dinner ___ been reserved too?'",
          options: ["Do / whether / has", "Could / if / was", "Do / if / is"],
          a: "Do / whether / has",
          tip: "Do you know = uncertain (not official staff). Whether = slightly formal. Has been reserved = Present Perfect — current status of reservation.",
          successTip: "DO YOU KNOW (uncertain — not official!) + WHETHER (slightly formal) + HAS BEEN RESERVED (Present Perfect, current!). The vocabulary: seat + reserved! 💺🍽️"
        },

        {
          type: "mc",
          q: "The restaurant host asks Nadia to confirm her reservation. She responds with a polite question: 'I made a reservation earlier — ___ ___ ___ ___ whether a table ___ reserved for Benali?'",
          options: ["could / you / check / and / tell / me / has been", "do / you / know / if / was", "could / you / confirm / whether / is"],
          a: "could / you / check / and / tell / me / has been",
          tip: "Could you check and tell me whether = extended polite form. Has been reserved = Present Perfect — current status.",
          successTip: "COULD YOU CHECK AND TELL ME WHETHER A TABLE HAS BEEN RESERVED — extended polite request! HAS BEEN RESERVED (Present Perfect) for a current reservation. Reserve a table collocation! 🍽️"
        },

        {
          type: "order",
          q: "Reorder: '¿Podría decirme si puedo reservar una mesa para ocho personas esta noche o si necesito reservar con antelación?'",
          words: ["Could", "you", "tell", "me", "whether", "I", "can", "reserve", "a", "table", "for", "eight", "tonight", "or", "whether", "I", "need", "to", "book", "in", "advance?"],
          a: "Could you tell me whether I can reserve a table for eight tonight or whether I need to book in advance?",
          extra: ["if", "could", "reserved"]
        },

        {
          type: "input",
          q: "Final hotel question: 'Could ___ ___ ___ whether the cost of ___ a table ___ already ___ to my room?' (tell me / reserving / has been added)",
          a: "you / tell / me / reserving / has been added",
          alts: ["you tell me whether the cost of reserving a table has been added"],
          successTip: "COULD YOU TELL ME WHETHER THE COST OF RESERVING A TABLE HAS BEEN ADDED — formal, present perfect, no inversion. COST + RESERVE A TABLE vocabulary combined! 💰🍽️✅"
        }
      ]
    },

    // ===================================================================
    // SET 5: DO YOU KNOW vs COULD YOU TELL ME — CONTRAST (B2-)
    // ===================================================================
    {
      id: "m10-u14-set5",
      title: "Set 5: Who Are You Asking?",
      context: `*London conference hotel — Day two*

Marcus had also arrived — a day late, luggage missing,
seat reassigned, flight diverted via Frankfurt.
He met Nadia in the lobby. She was calm.
"How are you so calm?" he asked.
"I know who to ask," she said, "and how to ask them."

*Hotel de la conferencia de Londres — Día dos*

Marcus también había llegado — un día tarde, equipaje perdido,
asiento reasignado, vuelo desviado vía Frankfurt.
Se encontró con Nadia en el vestíbulo. Ella estaba tranquila.
"¿Cómo estás tan tranquila?" preguntó.
"Sé a quién preguntar," dijo ella, "y cómo preguntarlo."`,

      questions: [
        {
          type: "match",
          q: "Match the openers to their social meaning:",
          pairs: [
            { left: "Do you know...?", right: "Asking someone who MAY or MAY NOT know — fellow passenger, stranger" },
            { left: "Could you tell me...?", right: "Asking someone who SHOULD know — staff, receptionist, official" },
            { left: "Direct question", right: "Informal, abrupt — fine between friends, can seem rude to strangers" }
          ],
          a: "completed",
          successTip: "The social register matters! Direct = informal/rude to strangers. Do you know = polite to anyone. Could you tell me = formal, implies the person has the answer. Same grammar inside! 🎭"
        },

        {
          type: "mc",
          q: "Marcus asks a fellow passenger: '___ you know ___ the next shuttle to the city center ___?'",
          options: ["Do / when / leaves", "Could / when / leaves", "Do / when / left"],
          a: "Do / when / leaves",
          tip: "Fellow passenger = Do you know (may not know). When + subject + verb. Present — live departure question.",
          successTip: "DO YOU KNOW (fellow passenger — uncertain!) + WHEN IT LEAVES (present, live!). No COULD YOU TELL ME — that would imply the passenger is official staff! 🚌"
        },

        {
          type: "mc",
          q: "Marcus asks the hotel concierge: '___ ___ ___ me ___ the shuttle to the conference center ___?'",
          options: ["Could / you / tell / when / leaves", "Do / you / know / when / leaves", "Could / you / tell / if / leaves"],
          a: "Could / you / tell / when / leaves",
          tip: "Hotel concierge = should know = Could you tell me. When + subject + verb. Present — live schedule.",
          successTip: "COULD YOU TELL ME (concierge — should know!) + WHEN IT LEAVES (present schedule). The same information, but asked more formally because of who is being asked! 🏨"
        },

        {
          type: "mc",
          q: "Marcus's luggage is missing. He asks a fellow traveller first, then the luggage desk. Choose the correct pair:",
          options: [
            "Fellow traveller: 'Do you know where the luggage claim office is?' / Desk: 'Could you tell me where my luggage is?'",
            "Fellow traveller: 'Could you tell me where the luggage claim office is?' / Desk: 'Do you know where my luggage is?'",
            "Fellow traveller: 'Do you know where is the luggage claim office?' / Desk: 'Could you tell me where is my luggage?'"
          ],
          a: "Fellow traveller: 'Do you know where the luggage claim office is?' / Desk: 'Could you tell me where my luggage is?'",
          tip: "Fellow traveller = may not know = Do you know. Luggage desk = should know = Could you tell me. Both with correct word order (IS at end)!",
          successTip: "DO YOU KNOW (traveller — uncertain) vs COULD YOU TELL ME (desk — should know!). Both with correct word order: IS at the end, not the beginning! 🧳🎭"
        },

        {
          type: "mc",
          q: "Nadia and Marcus are looking at the conference schedule. Nadia asks Marcus: '___ you know ___ our session ___ on the first or second floor?'",
          options: ["Do / whether / is", "Could / whether / is", "Do / if / was"],
          a: "Do / whether / is",
          tip: "Asking a colleague = Do you know (may not know). Whether...or = alternatives. Is = present — live question.",
          successTip: "DO YOU KNOW (colleague — uncertain!) + WHETHER...OR (alternatives = whether required!) + IS (present, live!). Three decisions, all correct! 📋"
        },

        {
          type: "mc",
          q: "Marcus asks the conference organiser: '___ ___ ___ me whether our session ___ delayed due to the speaker's late flight?'",
          options: ["Could / you / tell / has been", "Do / you / know / has been", "Could / you / tell / was"],
          a: "Could / you / tell / has been",
          tip: "Conference organiser = should know = Could you tell me. Whether = formal. Has been delayed = Present Perfect — current status.",
          successTip: "COULD YOU TELL ME (organiser — should know!) + WHETHER (formal) + HAS BEEN DELAYED (Present Perfect — current status!). FLIGHT + DELAYED vocabulary! ✈️⏰"
        },

        {
          type: "mc",
          q: "Choose the sentence that uses the WRONG opener for the context: A receptionist is being asked about room costs.",
          options: [
            "'Do you know how much the room costs?' — wrong, should be Could you tell me.",
            "'Could you tell me how much the room costs?' — correct for a receptionist.",
            "'Do you know how much the room costs?' — also acceptable, just less formal."
          ],
          a: "'Do you know how much the room costs?' — also acceptable, just less formal.",
          tip: "Both openers are grammatically correct — Do you know is just less formal than Could you tell me with staff.",
          successTip: "Important nuance! DO YOU KNOW is not WRONG with a receptionist — it's just LESS FORMAL. Both are grammatically correct. Could you tell me is preferred in formal contexts, but Do you know is never an error! 🎭"
        },

        {
          type: "order",
          q: "Reorder: '¿Podría decirme si mi equipaje ha llegado? Un pasajero me dijo que sabía dónde estaba, pero no estaba seguro.'",
          words: ["Could", "you", "tell", "me", "whether", "my", "luggage", "has", "arrived?", "A", "passenger", "told", "me", "he", "knew", "where", "it", "was,", "but", "he", "wasn't", "sure."],
          a: "Could you tell me whether my luggage has arrived? A passenger told me he knew where it was, but he wasn't sure.",
          extra: ["if", "was", "is"]
        },

        {
          type: "mc",
          q: "Marcus tells Nadia: 'I asked three people about the flight schedule.' Nadia: 'Who did you ask?' Marcus: 'A passenger, a cleaner, and the check-in desk.' Nadia: 'And what did you say to each one?' Marcus should have used:",
          options: [
            "'Do you know' for the passenger and cleaner — 'Could you tell me' for the check-in desk.",
            "'Could you tell me' for all three — it's always more polite.",
            "'Do you know' for all three — it's the same question."
          ],
          a: "'Do you know' for the passenger and cleaner — 'Could you tell me' for the check-in desk.",
          tip: "Passenger/cleaner = may not know = Do you know. Check-in desk = should know = Could you tell me.",
          successTip: "The register ladder: Passenger = DO YOU KNOW. Cleaner = DO YOU KNOW. Check-in desk = COULD YOU TELL ME. The question is the same — who you're asking determines how you ask! 🎭🛫"
        },

        {
          type: "input",
          q: "Nadia's advice to Marcus: 'To a stranger, say ___ ___ ___. To staff, say ___ ___ ___ ___. Same question — different opener.' (Do you know / Could you tell me)",
          a: "Do / you / know / Could / you / tell / me",
          alts: ["do / you / know / could / you / tell / me"],
          successTip: "DO YOU KNOW (stranger) vs COULD YOU TELL ME (staff). Same grammar inside, different social meaning outside. That's Nadia's whole travel philosophy! 🎭✈️✅"
        }
      ]
    },

    // ===================================================================
    // SET 6: WORD ORDER — THE CRITICAL RULE (B2)
    // ===================================================================
    {
      id: "m10-u14-set6",
      title: "Set 6: The Order That Makes It Polite",
      context: `*London — Marcus makes mistakes. Nadia corrects them.*

Marcus had been asking questions all day.
Most of them, Nadia noticed, were almost right.
"You're using the right opening," she told him,
"but then you're putting the verb in the wrong place."
"Show me," said Marcus.
"With pleasure," said Nadia.

*Londres — Marcus comete errores. Nadia los corrige.*

Marcus había estado haciendo preguntas todo el día.
La mayoría, notó Nadia, eran casi correctas.
"Estás usando la apertura correcta," le dijo,
"pero luego estás poniendo el verbo en el lugar equivocado."
"Muéstrame," dijo Marcus.
"Con mucho gusto," dijo Nadia.`,

      questions: [
        {
          type: "match",
          q: "Match the word order rules with examples:",
          pairs: [
            { left: "CORRECT", right: "'Do you know where my seat IS?' (subject + verb)" },
            { left: "INCORRECT — inversion", right: "'Do you know where IS my seat?' ❌" },
            { left: "INCORRECT — auxiliary", right: "'Do you know where DOES my seat go?' ❌" }
          ],
          a: "completed",
          successTip: "Two common errors: keeping the inversion (where IS my seat ❌) or keeping the auxiliary (where DOES it go ❌). Both disappear inside indirect questions! Subject + verb only! 📌"
        },

        {
          type: "mc",
          q: "Marcus said: 'Do you know where is the seat reservation desk?' Nadia corrected him:",
          options: [
            "Do you know where the seat reservation desk is?",
            "Do you know where the seat reservation desk was?",
            "Do you know where does the seat reservation desk is?"
          ],
          a: "Do you know where the seat reservation desk is?",
          tip: "Remove the inversion: where + subject (the desk) + verb (is). Present — live question.",
          successTip: "WHERE THE DESK IS ✅ (subject before verb!). WHERE IS THE DESK ❌ (inversion — direct question!). Marcus's error was keeping the direct question word order inside the polite structure! 📌💺"
        },

        {
          type: "mc",
          q: "Marcus: 'Could you tell me how much does it cost to reserve a table?' Nadia's correction:",
          options: [
            "Could you tell me how much it costs to reserve a table?",
            "Could you tell me how much it costed to reserve a table?",
            "Could you tell me how much costs it to reserve a table?"
          ],
          a: "Could you tell me how much it costs to reserve a table?",
          tip: "Remove DOES (auxiliary disappears). Subject (it) + verb (costs). Reserve a table = collocation.",
          successTip: "HOW MUCH IT COSTS ✅ — the auxiliary DOES disappears inside indirect questions! HOW MUCH DOES IT ❌ (direct question auxiliary!). RESERVE A TABLE collocation! 🍽️💰"
        },

        {
          type: "mc",
          q: "Marcus: 'Do you know when does the next canceled flight get rebooked?' Nadia:",
          options: [
            "Do you know when the next canceled flight gets rebooked?",
            "Do you know when does the next canceled flight rebooked?",
            "Do you know when the next canceled flight got rebooked?"
          ],
          a: "Do you know when the next canceled flight gets rebooked?",
          tip: "Remove DOES. Subject + verb (gets). Present — live question about current process. Canceled = vocabulary.",
          successTip: "WHEN THE FLIGHT GETS REBOOKED ✅ — DOES removed, subject + verb, present tense (live process!). CANCELED flight = vocabulary. Two corrections in one! ✈️"
        },

        {
          type: "mc",
          q: "Find ALL the errors: 'Could you tell me if is my luggage delayed or has it been canceled?'",
          options: [
            "Two errors: 'if is my luggage' should be 'if my luggage is' — and luggage can't be canceled (flights are canceled, luggage can be delayed or lost).",
            "One error: 'is my luggage' should be 'my luggage is'.",
            "One error: 'canceled' should be 'delayed' because luggage can't be canceled."
          ],
          a: "Two errors: 'if is my luggage' should be 'if my luggage is' — and luggage can't be canceled (flights are canceled, luggage can be delayed or lost).",
          tip: "Grammar error: inversion after if. Vocabulary error: luggage is delayed or lost — not canceled!",
          successTip: "TWO ERRORS! IF IS MY LUGGAGE ❌ (inversion — should be IF MY LUGGAGE IS) + LUGGAGE CANCELED ❌ (vocabulary — luggage is DELAYED or LOST, not canceled!). Grammar + vocabulary both tested! 🧳✈️"
        },

        {
          type: "mc",
          q: "Marcus tries again: 'Do you know if my delayed luggage has arrived yet?' Nadia says:",
          options: [
            "That's correct! Subject before verb, present perfect for current status, if for yes/no.",
            "Almost — 'has arrived' should be 'arrived'.",
            "Almost — 'if' should be 'whether' here."
          ],
          a: "That's correct! Subject before verb, present perfect for current status, if for yes/no.",
          tip: "IF + MY LUGGAGE + HAS ARRIVED (subject + verb, no inversion, correct tense) = all correct!",
          successTip: "CORRECT! IF (yes/no) + MY LUGGAGE (subject) + HAS ARRIVED (verb — no inversion!) + present perfect (current status). Marcus got it right! 🧳🎉"
        },

        {
          type: "mc",
          q: "Marcus: 'Could you tell me whether the table I reserved is ready or is still being prepared?' Nadia says:",
          options: [
            "That's correct — both clauses have subject + verb order.",
            "Error in the second clause: 'is still being prepared' should be 'still being prepared is'.",
            "Error: 'whether' should be 'if' after Could you tell me."
          ],
          a: "That's correct — both clauses have subject + verb order.",
          tip: "WHETHER + THE TABLE + IS READY and IS STILL BEING PREPARED = both have correct word order. Whether...or with two verb phrases.",
          successTip: "CORRECT AGAIN! WHETHER THE TABLE IS READY OR IS STILL BEING PREPARED — both clauses have subject + verb. Marcus is improving! WHETHER...OR + RESERVE A TABLE vocabulary! 🍽️🎉"
        },

        {
          type: "order",
          q: "Correct and reorder (the direct question has been mistakenly kept): 'Do you know where is the gate for delayed flights?' → Corrected:",
          words: ["Do", "you", "know", "where", "the", "gate", "for", "delayed", "flights", "is?"],
          a: "Do you know where the gate for delayed flights is?",
          extra: ["is where", "where is", "flights is"]
        },

        {
          type: "mc",
          q: "Marcus's final test. Four questions — how many are correct? A: 'Do you know where my seat is?' B: 'Could you tell me how much does the upgrade cost?' C: 'Do you know if the flight has been canceled?' D: 'Could you tell me where is the luggage claim?'",
          options: [
            "Two correct: A and C. B has auxiliary error (does). D has inversion error (where is).",
            "Three correct: A, B, and C. D has an inversion error.",
            "All four are correct."
          ],
          a: "Two correct: A and C. B has auxiliary error (does). D has inversion error (where is).",
          tip: "A: correct (where + IS). B: error (HOW MUCH DOES = direct auxiliary). C: correct (if + HAS BEEN). D: error (WHERE IS = inversion).",
          successTip: "A ✅ (where my seat IS) + C ✅ (if the flight HAS BEEN canceled). B ❌ (how much DOES = auxiliary kept) + D ❌ (where IS = inversion kept). Two right, two wrong! 📊"
        },

        {
          type: "input",
          q: "Marcus's corrected question D: 'Could you tell me ___ ___ luggage claim ___?' (where / the / is)",
          a: "where / the / is",
          alts: ["where the luggage claim is"],
          successTip: "WHERE THE LUGGAGE CLAIM IS ✅ — subject (the luggage claim) before verb (is). No inversion. Marcus has learned! LUGGAGE vocabulary! 🧳✈️✅"
        }
      ]
    },

    // ===================================================================
    // SET 7: NEGATIVE INDIRECT QUESTIONS (B2)
    // ===================================================================
    {
      id: "m10-u14-set7",
      title: "Set 7: Polite Negative Questions",
      context: `*London Heathrow — the following morning*

Marcus's return flight was also having problems.
The board showed nothing. The desk was closed.
The automated system gave no information.
His questions were getting more urgent — and more negative.
"Why hasn't the flight been announced?"
"Why isn't my seat showing on the system?"
Polite. Persistent. Precise.

*Aeropuerto de Heathrow de Londres — la mañana siguiente*

El vuelo de regreso de Marcus también tenía problemas.
El panel no mostraba nada. El mostrador estaba cerrado.
El sistema automático no daba información.
Sus preguntas se volvían más urgentes — y más negativas.
"¿Por qué no se ha anunciado el vuelo?"
"¿Por qué mi asiento no aparece en el sistema?"
Educado. Persistente. Preciso.`,

      questions: [
        {
          type: "match",
          q: "Match the negative indirect question structures:",
          pairs: [
            { left: "Why hasn't the flight been announced?", right: "Do you know why the flight hasn't been announced?" },
            { left: "Why isn't my seat showing?", right: "Could you tell me why my seat isn't showing?" },
            { left: "Why was the luggage delayed?", right: "Do you know why the luggage was delayed? (genuine past)" }
          ],
          a: "completed",
          successTip: "Negative indirect questions: same rule — subject + negative verb, no inversion! 'Why HASN'T the flight' → 'why the flight HASN'T'. The negative moves with the verb, after the subject! ⏪"
        },

        {
          type: "mc",
          q: "Direct: 'Why hasn't the flight to London been announced?' → Polite version:",
          options: [
            "Do you know why the flight to London hasn't been announced?",
            "Do you know why hasn't the flight to London been announced?",
            "Do you know why the flight to London wasn't announced?"
          ],
          a: "Do you know why the flight to London hasn't been announced?",
          tip: "Why + subject + negative verb (hasn't been announced). No inversion. Present Perfect — current status!",
          successTip: "WHY THE FLIGHT HASN'T BEEN ANNOUNCED ✅ — subject before negative verb! No inversion. HASN'T (not WASN'T — it's a current status question, still live!). ✈️"
        },

        {
          type: "mc",
          q: "Direct: 'Why isn't my seat showing on the system?' → Formal polite version:",
          options: [
            "Could you tell me why my seat isn't showing on the system?",
            "Could you tell me why isn't my seat showing on the system?",
            "Could you tell me why my seat wasn't showing on the system?"
          ],
          a: "Could you tell me why my seat isn't showing on the system?",
          tip: "Could you tell me (staff). Why + subject + negative verb (isn't). Present — current system status!",
          successTip: "WHY MY SEAT ISN'T SHOWING ✅ — subject before negative verb, IS (present — still a live problem!). COULD YOU TELL ME (staff should know). 💺"
        },

        {
          type: "mc",
          q: "Marcus asks a fellow traveller: '___ you know ___ the cost of re-booking ___ been confirmed yet?'",
          options: ["Do / whether / hasn't", "Could / whether / hasn't", "Do / if / wasn't"],
          a: "Do / whether / hasn't",
          tip: "Fellow traveller = Do you know. Whether (slightly formal for cost/rebooking context). Hasn't been confirmed = negative Present Perfect, current.",
          successTip: "DO YOU KNOW (fellow traveller — uncertain!) + WHETHER (slightly formal — rebooking cost) + HASN'T BEEN CONFIRMED (negative Present Perfect — still not confirmed!). 💰"
        },

        {
          type: "mc",
          q: "Choose the correct negative indirect question: Original — 'Why haven't they collected the delayed luggage?'",
          options: [
            "Do you know why they haven't collected the delayed luggage?",
            "Do you know why haven't they collected the delayed luggage?",
            "Do you know why they didn't collect the delayed luggage?"
          ],
          a: "Do you know why they haven't collected the delayed luggage?",
          tip: "Why + subject (they) + negative verb (haven't collected). No inversion. Haven't = present — still an ongoing issue!",
          successTip: "WHY THEY HAVEN'T COLLECTED ✅ — no inversion, subject first. HAVEN'T (not DIDN'T — it's still an ongoing situation, not a completed past event!). DELAYED LUGGAGE vocabulary! 🧳"
        },

        {
          type: "mc",
          q: "Marcus asks the desk about a canceled flight: '___ ___ ___ me ___ the flight ___ rescheduled yet?'",
          options: ["Could / you / tell / whether / hasn't been", "Do / you / know / if / wasn't", "Could / you / tell / whether / isn't"],
          a: "Could / you / tell / whether / hasn't been",
          tip: "Staff = Could you tell me. Whether (formal). Hasn't been rescheduled = negative Present Perfect — still a live problem.",
          successTip: "COULD YOU TELL ME (staff!) + WHETHER (formal) + HASN'T BEEN RESCHEDULED (negative Present Perfect — still ongoing!). CANCELED FLIGHT vocabulary! ✈️"
        },

        {
          type: "mc",
          q: "Compare: 'Do you know why the flight wasn't delayed?' vs 'Do you know why the flight isn't delayed?' What's the difference?",
          options: [
            "'Wasn't delayed' = asking about a past flight (genuinely past). 'Isn't delayed' = asking about the current flight status (live).",
            "'Wasn't delayed' is incorrect — always use isn't.",
            "No difference — both mean the same thing."
          ],
          a: "'Wasn't delayed' = asking about a past flight (genuinely past). 'Isn't delayed' = asking about the current flight status (live).",
          tip: "The tense tells you whether the flight is past (was/wasn't) or current (is/isn't). Not backshift — real tense choice!",
          successTip: "REAL TENSE CHOICE (not backshift!). WASN'T DELAYED = the flight already landed (past). ISN'T DELAYED = the flight is currently operating (present). Context determines tense in live questions! 🔑✈️"
        },

        {
          type: "order",
          q: "Reorder: '¿Podría decirme por qué mi equipaje no ha llegado todavía y cuándo está previsto que llegue?'",
          words: ["Could", "you", "tell", "me", "why", "my", "luggage", "hasn't", "arrived", "yet", "and", "when", "it", "is", "expected", "to", "arrive?"],
          a: "Could you tell me why my luggage hasn't arrived yet and when it is expected to arrive?",
          extra: ["hasn't it", "when is", "arrived yet"]
        },

        {
          type: "mc",
          q: "Marcus's most frustrated (but still polite!) question: '___ you tell me ___ my seat ___ been reassigned without my permission AND ___ the cost of the upgrade ___ been refunded?'",
          options: ["Could / why / has / whether / hasn't", "Do / why / was / whether / wasn't", "Could / why / had / if / hadn't"],
          a: "Could / why / has / whether / hasn't",
          tip: "Staff = Could you tell me. Why + has been reassigned (present — still the situation). Whether + hasn't been refunded (yes/no negative — current).",
          successTip: "COULD YOU TELL ME (staff!) + WHY + HAS BEEN REASSIGNED (present — current issue) + WHETHER + HASN'T BEEN REFUNDED (yes/no negative — current). SEAT + COST vocabulary! 💺💰"
        },

        {
          type: "input",
          q: "Final angry-but-polite question: 'Could you tell me ___ my delayed luggage ___ arrived and ___ the cost of delivery ___ been confirmed?' (why + not yet / whether + hasn't)",
          a: "why / hasn't / whether / hasn't",
          alts: ["why my delayed luggage hasn't arrived / whether the cost hasn't been confirmed"],
          successTip: "WHY [my luggage] HASN'T ARRIVED + WHETHER [the cost] HASN'T BEEN CONFIRMED — two negative indirect questions in one polite sentence. Angry but perfect grammar! 🧳💰✅"
        }
      ]
    },

    // ===================================================================
    // SET 8: SEQUENCES OF POLITE QUESTIONS (B2)
    // ===================================================================
    {
      id: "m10-u14-set8",
      title: "Set 8: A Full Conversation",
      context: `*London Heathrow — Marcus at the rebooking desk*

Marcus had found the rebooking desk.
There was one staff member and a very long queue.
When he finally reached the front,
he had eight questions — about his flight, his seat,
his luggage, the cost, and the table he had booked for dinner
that he was now going to miss.
He had exactly three minutes.

*Aeropuerto de Heathrow de Londres — Marcus en el mostrador de cambios*

Marcus había encontrado el mostrador de cambios.
Había un empleado y una cola muy larga.
Cuando por fin llegó al principio,
tenía ocho preguntas — sobre su vuelo, su asiento,
su equipaje, el coste y la mesa que había reservado para cenar
y que ahora iba a perderse.
Tenía exactamente tres minutos.`,

      questions: [
        {
          type: "mc",
          q: "Question 1 — Flight status: Marcus needs to know if his flight has been rescheduled. He says:",
          options: [
            "Could you tell me whether my flight has been rescheduled?",
            "Could you tell me whether has my flight been rescheduled?",
            "Do you know whether my flight was rescheduled?"
          ],
          a: "Could you tell me whether my flight has been rescheduled?",
          tip: "Staff = Could you tell me. Whether = formal yes/no. Has been rescheduled = Present Perfect — current status.",
          successTip: "COULD YOU TELL ME (staff) + WHETHER (formal) + HAS BEEN RESCHEDULED (Present Perfect — current!). No inversion. Question 1 ✅! ✈️"
        },

        {
          type: "mc",
          q: "Question 2 — New departure time: He needs the new departure time. He says:",
          options: [
            "Could you tell me when the new flight departs?",
            "Could you tell me when does the new flight depart?",
            "Could you tell me when the new flight departed?"
          ],
          a: "Could you tell me when the new flight departs?",
          tip: "When + subject + verb (departs — no does!). Present — live schedule. No auxiliary inside!",
          successTip: "WHEN THE FLIGHT DEPARTS ✅ — no DOES, present tense (live schedule!). Question 2 ✅! ✈️⏰"
        },

        {
          type: "mc",
          q: "Question 3 — Seat: His seat was reassigned. He asks:",
          options: [
            "Could you tell me which seat I have been assigned?",
            "Could you tell me which seat have I been assigned?",
            "Could you tell me which seat was I assigned?"
          ],
          a: "Could you tell me which seat I have been assigned?",
          tip: "Which + subject (I) + verb (have been assigned). No inversion. Present Perfect — current assignment.",
          successTip: "WHICH SEAT I HAVE BEEN ASSIGNED ✅ — subject (I) before verb (have been). Present Perfect (current assignment!). Question 3 ✅! 💺"
        },

        {
          type: "mc",
          q: "Question 4 — Luggage: His luggage is missing. He asks:",
          options: [
            "Could you tell me where my luggage is?",
            "Could you tell me where is my luggage?",
            "Do you know where my luggage was?"
          ],
          a: "Could you tell me where my luggage is?",
          tip: "Staff = Could you tell me. Where + subject + verb (is). Present — luggage is currently missing!",
          successTip: "WHERE MY LUGGAGE IS ✅ — subject before verb, IS (present — still missing!). Question 4 ✅! 🧳"
        },

        {
          type: "mc",
          q: "Question 5 — Cost: He needs to know the cost of the changes. He asks:",
          options: [
            "Could you tell me how much all these changes cost?",
            "Could you tell me how much do all these changes cost?",
            "Could you tell me how much all these changes costed?"
          ],
          a: "Could you tell me how much all these changes cost?",
          tip: "How much + subject + verb (cost — no do!). Present — live pricing. No auxiliary inside!",
          successTip: "HOW MUCH THESE CHANGES COST ✅ — no DO, present tense! COST (not COSTED — cost is irregular, past = cost too, but here it's present tense!). Question 5 ✅! 💰"
        },

        {
          type: "mc",
          q: "Question 6 — Why: He wants to know why his seat was changed without warning. He asks:",
          options: [
            "Could you tell me why my seat was changed without warning?",
            "Could you tell me why was my seat changed without warning?",
            "Could you tell me why my seat is changed without warning?"
          ],
          a: "Could you tell me why my seat was changed without warning?",
          tip: "Why + subject + verb (was changed). Was = genuine past — the change already happened! No inversion.",
          successTip: "WHY MY SEAT WAS CHANGED ✅ — WAS (genuine past — it already happened!). Not a live question — a past event. Subject before verb. Question 6 ✅! 💺🔍"
        },

        {
          type: "mc",
          q: "Question 7 — Table: He had a restaurant reservation for tonight. He asks:",
          options: [
            "Could you tell me whether the restaurant can hold my table reservation until tomorrow?",
            "Could you tell me whether the restaurant could hold my table reservation until tomorrow?",
            "Could you tell me if can the restaurant hold my table reservation until tomorrow?"
          ],
          a: "Could you tell me whether the restaurant can hold my table reservation until tomorrow?",
          tip: "Whether (formal yes/no). Can = present ability — can they do this now? No inversion after whether.",
          successTip: "WHETHER THE RESTAURANT CAN HOLD ✅ — CAN (present ability, live question!) + no inversion. RESERVE A TABLE vocabulary (table reservation)! Question 7 ✅! 🍽️"
        },

        {
          type: "order",
          q: "Reorder Question 8 — the final summary question: '¿Podría decirme en pocas palabras si el vuelo ha sido cancelado o simplemente retrasado, y cuándo puedo esperar salir?'",
          words: ["Could", "you", "tell", "me", "briefly", "whether", "the", "flight", "has", "been", "canceled", "or", "simply", "delayed,", "and", "when", "I", "can", "expect", "to", "depart?"],
          a: "Could you tell me briefly whether the flight has been canceled or simply delayed, and when I can expect to depart?",
          extra: ["if", "was", "could"]
        },

        {
          type: "mc",
          q: "After the desk closed, Marcus reported the conversation to Nadia. He said: 'I asked ___ the flight ___ rescheduled, ___ my seat ___, and ___ my luggage ___.' (reported speech — backshift applies!)",
          options: [
            "whether / had been / where / was / where / was",
            "whether / has been / where / is / where / is",
            "if / was / what / had been / where / is"
          ],
          a: "whether / had been / where / was / where / was",
          tip: "NOW it's reported speech (past context) — backshift applies! Has been → had been. Is → was. This is the key contrast with live indirect questions!",
          successTip: "NOW IT'S REPORTED SPEECH! HAD BEEN (backshift from has been) + WAS (backshift from is). Compare: LIVE question = 'Could you tell me whether it HAS BEEN' / REPORTED = 'He asked whether it HAD BEEN'. The contrast in action! 🔑"
        },

        {
          type: "input",
          q: "Nadia's summary: 'The key rule for indirect questions: ___ ___ ___ — the tense stays the same. In reported speech: ___ ___ ___ — the tense moves back.' (no backshift / backshift applies)",
          a: "no / backshift / applies / backshift / applies",
          alts: ["No backshift / backshift applies"],
          successTip: "THE CORE RULE! Live indirect questions = NO BACKSHIFT (the situation is current). Reported speech = BACKSHIFT (the situation was in the past). Same grammar structure, different time context = different tense! 🔑✅"
        }
      ]
    },

    // ===================================================================
    // SET 9: DIRECT vs INDIRECT vs REPORTED — REGISTER LADDER (B2)
    // ===================================================================
    {
      id: "m10-u14-set9",
      title: "Set 9: Three Ways to Ask",
      context: `*London — Nadia explains to Marcus*

Marcus was still frustrated with his grammar.
"I keep confusing the indirect question with the reported question,"
he told Nadia. "They look the same but they're different."
"They are different," said Nadia. "Let me show you the three versions —
direct, indirect, and reported. Same question. Three registers. Three rules."
Marcus listened carefully. For once.

*Londres — Nadia le explica a Marcus*

Marcus seguía frustrado con su gramática.
"Sigo confundiendo la pregunta indirecta con la pregunta reportada,"
le dijo a Nadia. "Se parecen, pero son diferentes."
"Son diferentes," dijo Nadia. "Déjame mostrarte las tres versiones —
directa, indirecta y reportada. La misma pregunta. Tres registros. Tres reglas."
Marcus escuchó con atención. Por una vez.`,

      questions: [
        {
          type: "match",
          q: "Match the three question types to their rules:",
          pairs: [
            { left: "Direct", right: "Where IS the gate? — inversion, auxiliary, informal/abrupt" },
            { left: "Indirect (live)", right: "Do you know where the gate IS? — no inversion, no backshift, polite" },
            { left: "Reported (past)", right: "She asked where the gate WAS — no inversion, backshift, past context" }
          ],
          a: "completed",
          successTip: "Three types! Direct = inversion (IS the gate). Indirect = no inversion, NO backshift (the gate IS — live!). Reported = no inversion, BACKSHIFT (the gate WAS — past context!). 🔑"
        },

        {
          type: "mc",
          q: "Choose the correct set for the register ladder — direct → indirect → reported:",
          options: [
            "Is the flight delayed? / Do you know if the flight is delayed? / She asked if the flight was delayed.",
            "Is the flight delayed? / Do you know if the flight was delayed? / She asked if the flight is delayed.",
            "Is the flight delayed? / Do you know if the flight is delayed? / She asked if the flight is delayed."
          ],
          a: "Is the flight delayed? / Do you know if the flight is delayed? / She asked if the flight was delayed.",
          tip: "Direct = IS. Indirect (live) = IS (no backshift). Reported (past) = WAS (backshift). Three versions, three tenses!",
          successTip: "PERFECT REGISTER LADDER! IS (direct) → IS (indirect, live) → WAS (reported, past). The indirect and reported look almost identical — but the tense reveals the context! 🔑✈️"
        },

        {
          type: "mc",
          q: "Nadia asks at the hotel (live situation): '___ ___ ___ me how much the room costs.' → The SAME question reported later to Marcus:",
          options: [
            "Live: 'Could you tell me how much the room costs.' / Reported: 'She asked how much the room cost.'",
            "Live: 'Could you tell me how much the room cost.' / Reported: 'She asked how much the room costs.'",
            "Live: 'Could you tell me how much the room costs.' / Reported: 'She asked how much the room costs.'"
          ],
          a: "Live: 'Could you tell me how much the room costs.' / Reported: 'She asked how much the room cost.'",
          tip: "Live indirect = costs (present). Reported later = cost (backshifted past simple from costs). Same question — different time context!",
          successTip: "COSTS (live — current price) → COST (reported — backshifted). Same question, same person, different time context = different tense. The key distinction! 💰🔑"
        },

        {
          type: "mc",
          q: "Marcus is currently at the airport. He asks: '___ you know ___ the canceled flight ___ rebooked?' — Which tense is correct?",
          options: [
            "Do / if / has been (Present Perfect — live question, current status)",
            "Do / if / had been (Past Perfect — live question)",
            "Do / if / was (Past Simple — backshift)"
          ],
          a: "Do / if / has been (Present Perfect — live question, current status)",
          tip: "Live question at the airport right now = no backshift. Has been rebooked = Present Perfect for current status.",
          successTip: "HAS BEEN REBOOKED ✅ — live question, Present Perfect for current status. HAD BEEN ❌ (that's backshift — only for reported speech!). The distinction in action! ✈️🔑"
        },

        {
          type: "mc",
          q: "Marcus reports the conversation to Nadia an hour later: 'I asked the desk ___ the canceled flight ___ rebooked.' What changes?",
          options: [
            "'had been rebooked' — now it's reported speech, so backshift applies.",
            "'has been rebooked' — same tense, no change needed.",
            "'was rebooked' — simpler past is always used in reported speech."
          ],
          a: "'had been rebooked' — now it's reported speech, so backshift applies.",
          tip: "Reporting the past conversation = reported speech = backshift. Has been → had been.",
          successTip: "NOW IT'S REPORTED SPEECH (past context!) → HAD BEEN REBOOKED (backshifted from has been). Live at airport = HAS BEEN. Reported later = HAD BEEN. The tense shift that reveals everything! 🔑"
        },

        {
          type: "mc",
          q: "Choose the sentence that shows the correct contrast between indirect and reported questions:",
          options: [
            "Indirect (live): 'Do you know where the luggage IS?' / Reported: 'He asked where the luggage WAS.'",
            "Indirect (live): 'Do you know where the luggage WAS?' / Reported: 'He asked where the luggage IS.'",
            "Both use IS — there's no difference between indirect and reported."
          ],
          a: "Indirect (live): 'Do you know where the luggage IS?' / Reported: 'He asked where the luggage WAS.'",
          tip: "IS = live (current situation). WAS = reported (past context, backshifted). Same structure — different tense = different context.",
          successTip: "IS (live — luggage is currently missing!) vs WAS (reported — he asked about it in the past). The tense IS the meaning here. 🧳🔑"
        },

        {
          type: "mc",
          q: "Nadia tests Marcus: 'I'm standing at the check-in desk right now. I want to ask if my seat is available. Which version do I use?'",
          options: [
            "'Could you tell me whether my seat is available?' (indirect — live situation, present tense)",
            "'She asked whether her seat was available.' (reported — past context)",
            "'Is my seat available?' (direct — also fine, but less polite)"
          ],
          a: "'Could you tell me whether my seat is available?' (indirect — live situation, present tense)",
          tip: "Standing at the desk right now = live situation = indirect question = present tense (IS available).",
          successTip: "COULD YOU TELL ME WHETHER MY SEAT IS AVAILABLE ✅ — live situation = present tense = no backshift. Could you tell me = staff should know. SEAT vocabulary! 💺🔑"
        },

        {
          type: "order",
          q: "Reorder the THREE versions: Direct / Indirect / Reported for: 'Is the table reserved?'",
          words: ["Direct:", "Is", "the", "table", "reserved?", "/", "Indirect:", "Do", "you", "know", "whether", "the", "table", "is", "reserved?", "/", "Reported:", "She", "asked", "whether", "the", "table", "was", "reserved."],
          a: "Direct: Is the table reserved? / Indirect: Do you know whether the table is reserved? / Reported: She asked whether the table was reserved.",
          extra: ["was reserved?", "is reserved.", "the table is"]
        },

        {
          type: "mc",
          q: "Final test: Marcus says 'I asked the receptionist if the table was reserved.' Nadia says 'And what did you say to the receptionist exactly?' Marcus should answer:",
          options: [
            "'Could you tell me whether the table is reserved?' (indirect — live at the desk)",
            "'Could you tell me whether the table was reserved?' (reported — already past)",
            "'I asked if the table was reserved.' (this is how he reported it, not what he said)"
          ],
          a: "'Could you tell me whether the table is reserved?' (indirect — live at the desk)",
          tip: "At the desk in real time = live indirect question = IS (present). What he said = indirect. What he reports = reported speech with WAS.",
          successTip: "AT THE DESK (live) = IS RESERVED. REPORTING LATER = WAS RESERVED. Marcus said 'whether the table IS reserved' — he's now reporting that as 'whether the table WAS reserved'. Perfect! 🍽️🔑✅"
        },

        {
          type: "input",
          q: "Complete Nadia's rule: 'Indirect question: ___ + subject + verb (present). Reported question: ___ + subject + verb (___).' (Do you know/Could you tell me / asked/wondered / past)",
          a: "Do you know/Could you tell me / asked/wondered / past",
          alts: ["Do you know / asked / past", "Could you tell me / asked / past"],
          successTip: "THE COMPLETE RULE! Indirect (live) = Do you know/Could you tell me + subject + verb (PRESENT). Reported (past) = asked/wondered + subject + verb (PAST/backshifted). Same structure — different time = different tense! 🔑✅"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL NARRATIVE (B2)
    // ===================================================================
    {
      id: "m10-u14-set10",
      title: "Set 10: One Day, Every Question",
      context: `*Casablanca to London — the full journey*

In twenty-four hours, Nadia had asked about flights, seats,
luggage, costs, tables, delays, and cancelations.
She had asked passengers, staff, concierges, and hosts.
She had used direct questions with friends,
'Do you know' with strangers,
and 'Could you tell me' with anyone who should know the answer.
Every question had been polite. Every question had been answered.

*Casablanca a Londres — el viaje completo*

En veinticuatro horas, Nadia había preguntado sobre vuelos, asientos,
equipaje, costes, mesas, retrasos y cancelaciones.
Había preguntado a pasajeros, al personal, a conserjes y a anfitriones.
Había usado preguntas directas con amigos,
"¿Sabe usted?" con extraños,
y "¿Podría decirme?" con cualquiera que debería saber la respuesta.
Cada pregunta había sido educada. Cada pregunta había sido respondida.`,

      questions: [
        {
          type: "mc",
          q: "At Casablanca airport (6am), Nadia asks a stranger: '___ ___ ___ the departures board ___ updating?'",
          options: ["Do / you / know / if / is", "Could / you / tell / me / whether / is", "Do / you / know / whether / is"],
          a: "Do / you / know / if / is",
          tip: "Stranger at airport = Do you know (may not know). If = informal. Is = present — current status of the board.",
          successTip: "DO YOU KNOW (stranger — uncertain!) + IF (informal) + IS UPDATING (present, live!). The first polite question of the journey! ✈️"
        },

        {
          type: "mc",
          q: "At the information desk (6:30am), Nadia asks staff: '___ ___ ___ me why the flight to London ___ been delayed?'",
          options: ["Could / you / tell / has", "Do / you / know / has", "Could / you / tell / had"],
          a: "Could / you / tell / has",
          tip: "Staff = Could you tell me (should know). Why + subject + has been delayed (Present Perfect — current status). No inversion!",
          successTip: "COULD YOU TELL ME (staff!) + WHY THE FLIGHT + HAS BEEN DELAYED (Present Perfect — still a current situation!). FLIGHT + DELAYED vocabulary! ✈️⏰"
        },

        {
          type: "mc",
          q: "At the gate (8am), Nadia asks a fellow passenger: '___ you know ___ the flight ___ canceled or just delayed?'",
          options: ["Do / whether / has been / or", "Could / whether / is / or", "Do / if / is / or"],
          a: "Do / whether / has been / or",
          tip: "Fellow passenger = Do you know. Whether...or (alternatives = whether, not if!). Has been canceled or delayed = Present Perfect — current status.",
          successTip: "DO YOU KNOW (fellow passenger!) + WHETHER...OR (alternatives = whether required!) + HAS BEEN (Present Perfect — current!). CANCELED + DELAYED vocabulary! ✈️"
        },

        {
          type: "mc",
          q: "On the plane (noon), Nadia asks the flight attendant: '___ ___ ___ me ___ my seat ___ been changed from 14A?'",
          options: ["Could / you / tell / whether / has", "Do / you / know / if / has", "Could / you / tell / if / was"],
          a: "Could / you / tell / whether / has",
          tip: "Flight attendant = Could you tell me (should know). Whether = formal yes/no. Has been changed = Present Perfect — current assignment.",
          successTip: "COULD YOU TELL ME (flight attendant!) + WHETHER (formal) + HAS BEEN CHANGED (Present Perfect — current seating!). SEAT vocabulary! 💺"
        },

        {
          type: "mc",
          q: "At Heathrow luggage claim (3pm): '___ ___ ___ me ___ carousel my luggage from flight CA203 ___?'",
          options: ["Could / you / tell / which / is on", "Do / you / know / what / is on", "Could / you / tell / where / is"],
          a: "Could / you / tell / which / is on",
          tip: "Baggage staff = Could you tell me. Which carousel = WH-. Is on = present — luggage is (hopefully!) there now.",
          successTip: "COULD YOU TELL ME (staff!) + WHICH CAROUSEL + IS ON (present — current location!). LUGGAGE vocabulary! 🧳"
        },

        {
          type: "mc",
          q: "At hotel check-in (5pm): '___ ___ ___ me ___ the cost ___ luggage storage ___ included?'",
          options: ["Could / you / tell / whether / of / is", "Do / you / know / if / for / is", "Could / you / tell / what / for / is"],
          a: "Could / you / tell / whether / of / is",
          tip: "Receptionist = Could you tell me. Whether = formal yes/no. The cost of luggage storage IS included = present — current pricing.",
          successTip: "COULD YOU TELL ME (receptionist!) + WHETHER (formal) + THE COST OF LUGGAGE STORAGE + IS INCLUDED (present — current policy!). COST + LUGGAGE vocabulary! 💰🧳"
        },

        {
          type: "mc",
          q: "At the restaurant (7pm): '___ ___ ___ me ___ I ___ reserve a table for eight ___ whether I need to ___ in advance?'",
          options: ["Could / you / tell / if / can / or / book", "Do / you / know / whether / could / or / booked", "Could / you / tell / whether / can / or / book"],
          a: "Could / you / tell / whether / can / or / book",
          tip: "Restaurant host = Could you tell me. Whether (formal) + or (alternatives). Can = present ability. Book in advance = parallel infinitive.",
          successTip: "COULD YOU TELL ME WHETHER I CAN RESERVE A TABLE OR BOOK IN ADVANCE — formal, WHETHER...OR (alternatives!), CAN (present ability). RESERVE A TABLE vocabulary! 🍽️"
        },

        {
          type: "mc",
          q: "That evening, Nadia reports her day to Marcus. She says: 'I asked the information desk why the flight ___ delayed.' The tense changes because:",
          options: [
            "Now it's reported speech (past context) — IS → WAS (backshift applies).",
            "She uses was because the flight was delayed in the past.",
            "She uses was because all reported speech always uses past simple."
          ],
          a: "Now it's reported speech (past context) — IS → WAS (backshift applies).",
          tip: "Reporting a past conversation = reported speech = backshift. At the time she said IS (live). Reporting later = WAS.",
          successTip: "AT THE DESK (live) = 'Could you tell me why the flight IS delayed.' REPORTING LATER = 'I asked why the flight WAS delayed.' Backshift only applies in reported speech — not in live indirect questions! 🔑"
        },

        {
          type: "order",
          q: "Reorder Nadia's summary of the day: 'Pregunté al personal si el vuelo había sido cancelado, dónde estaba mi equipaje, cuánto costaba el almacenaje y si podía reservar una mesa para mis colegas.'",
          words: ["I", "asked", "the", "staff", "whether", "the", "flight", "had", "been", "canceled,", "where", "my", "luggage", "was,", "how", "much", "storage", "cost,", "and", "whether", "I", "could", "reserve", "a", "table", "for", "my", "colleagues."],
          a: "I asked the staff whether the flight had been canceled, where my luggage was, how much storage cost, and whether I could reserve a table for my colleagues.",
          extra: ["if", "has been", "is"]
        },

        {
          type: "input",
          q: "Marcus's lesson learned: 'When I'm ___ the question live, I use the ___ tense. When I'm ___ the question later, I use the ___ tense. Same structure — different ___.' (asking / present / reporting / past / context)",
          a: "asking / present / reporting / past / context",
          alts: ["Asking / present / reporting / past / context"],
                    successTip: "🏆 UNIT 14 COMPLETE! Do you know + WH ✅ Could you tell me + WH ✅ If/whether in indirect questions ✅ No inversion ✅ No backshift (live) ✅ Backshift in reported speech ✅ Direct vs indirect vs reported contrast ✅ All vocabulary: flight · delayed · canceled · luggage · seat · cost · reserve a table ✅ You ask — politely, precisely, perfectly! ✈️🧳🍽️🏆"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u14-reading",
    title: "Reading: Nadia's Perfect Day of Questions",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**At the Information Desk**

Nadia approached the information desk and asked politely: "Could you tell me whether the flight to London has been delayed or canceled?" The staff member checked the screen and said the flight was delayed by three hours. Nadia then asked where the luggage storage area was, and the staff member directed her to the lower level.`
      },
      {
        type: "mc",
        q: "What did Nadia ask at the information desk?",
        options: [
          "Whether the flight had been delayed or canceled",
          "Whether she could reserve a seat on another flight",
          "How much the luggage storage cost"
        ],
        a: "Whether the flight had been delayed or canceled",
        tip: "'Could you tell me whether the flight to London has been delayed or canceled?'"
      },
      {
        type: "mc",
        q: "What was the second thing Nadia asked about?",
        options: [
          "Where the luggage storage area was",
          "What the cost of the delay compensation was",
          "Whether her seat had been reassigned"
        ],
        a: "Where the luggage storage area was",
        tip: "'Nadia then asked where the luggage storage area was.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**At the Gate**

At the gate, Nadia turned to a fellow passenger and asked: "Do you know whether the flight has been rescheduled or whether we're still waiting for news?" The passenger shook his head. He didn't know either. Nadia asked a gate agent: "Could you tell me when we can expect to board?" The agent said boarding would begin in forty minutes.`
      },
      {
        type: "mc",
        q: "Why did Nadia use 'Do you know' with the fellow passenger?",
        options: [
          "Because the passenger might not have the information",
          "Because 'Do you know' is always more formal",
          "Because the flight had already been canceled"
        ],
        a: "Because the passenger might not have the information",
        tip: "'Do you know' is used when the person may or may not know — 'Could you tell me' is for staff who should know."
      },
      {
        type: "mc",
        q: "What did Nadia ask the gate agent?",
        options: [
          "When they could expect to board",
          "Whether the flight had been canceled",
          "How much a seat upgrade cost"
        ],
        a: "When they could expect to board",
        tip: "'Could you tell me when we can expect to board?'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**At the Hotel**

At the hotel reception, Nadia asked: "Could you tell me whether luggage storage is included in the room rate?" The receptionist confirmed it was. She also asked whether she could reserve a table for eight at the restaurant, and whether the cost of the dinner would be added to her room bill or paid separately.`
      },
      {
        type: "mc",
        q: "What two things did Nadia ask about at the hotel?",
        options: [
          "Whether luggage storage was included, and whether she could reserve a table",
          "Whether her flight had been delayed, and whether her seat had changed",
          "How much the room cost, and whether the flight had been canceled"
        ],
        a: "Whether luggage storage was included, and whether she could reserve a table",
        tip: "'Could you tell me whether luggage storage is included...?' and 'whether she could reserve a table for eight.'"
      },
      {
        type: "mc",
        q: "What was Nadia's question about the dinner cost?",
        options: [
          "Whether it would be added to her room bill or paid separately",
          "Whether she could reserve the table for free",
          "Whether the restaurant was on the ground floor or first floor"
        ],
        a: "Whether it would be added to her room bill or paid separately",
        tip: "'Whether the cost of the dinner would be added to her room bill or paid separately' — whether...or for alternatives!"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Marcus's Mistakes**

Marcus had asked the same questions — but not always correctly. He asked: "Do you know where is my seat?" and "Could you tell me how much does the upgrade cost?" Nadia corrected him patiently. "The verb comes after the subject," she said. "And the auxiliary disappears inside the question." Marcus tried again. This time, he got it right.`
      },
      {
        type: "mc",
        q: "What were Marcus's two grammatical errors?",
        options: [
          "Inversion after 'where' and keeping the auxiliary 'does'",
          "Using 'Do you know' instead of 'Could you tell me'",
          "Using the wrong tense and wrong vocabulary"
        ],
        a: "Inversion after 'where' and keeping the auxiliary 'does'",
        tip: "'Where IS my seat' (inversion) and 'how much DOES the upgrade cost' (auxiliary kept) — both errors inside indirect questions."
      },
      {
        type: "mc",
        q: "What were Nadia's two correction rules?",
        options: [
          "The verb comes after the subject, and the auxiliary disappears",
          "Use 'whether' not 'if', and always backshift the tense",
          "Use 'Could you tell me' not 'Do you know', and keep the auxiliary"
        ],
        a: "The verb comes after the subject, and the auxiliary disappears",
        tip: "'The verb comes after the subject' (no inversion) + 'the auxiliary disappears inside the question' (no does/did/is)."
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Key Difference**

That evening, Nadia explained the most important rule to Marcus: "When you ask a live question — at a desk, at a gate, in a restaurant — you keep the present tense. 'Do you know if the flight is delayed?' But when you report the conversation later, the tense moves back. 'I asked if the flight was delayed.' Same structure. Different time. Different tense."`
      },
      {
        type: "mc",
        q: "What tense do you use in a live indirect question?",
        options: [
          "Present tense — the situation is happening now",
          "Past tense — always backshift in indirect questions",
          "Past perfect — the most formal option"
        ],
        a: "Present tense — the situation is happening now",
        tip: "'When you ask a live question... you keep the present tense.' No backshift — it's a current situation!"
      },
      {
        type: "mc",
        q: "What changes when you report the conversation later?",
        options: [
          "The tense moves back — present becomes past",
          "The opener changes from 'Do you know' to 'She asked'",
          "Both the opener and the tense change"
        ],
        a: "The tense moves back — present becomes past",
        tip: "'When you report the conversation later, the tense moves back.' IS → WAS. Same structure — different time context!",
        successTip: "Both change! The opener (Do you know → She asked) AND the tense (is → was). But the key rule Nadia highlighted is the tense shift! 🔑✈️"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2)
  // ===================================================================
  reviewSet: {
    id: "m10-u14-review",
    title: "Unit 14 Review: Indirect Questions + Travel Vocabulary",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: '___ you know ___ the flight to London ___ delayed?'",
        options: ["Do / if / is", "Do / if / was", "Could / if / is"],
        a: "Do / if / is",
        tip: "Stranger = Do you know. If = informal yes/no. Is = present — live question about current status."
      },
      {
        type: "mc",
        q: "Review: '___ ___ ___ me where ___ luggage carousel ___ for flight CA203.'",
        options: ["Could / you / tell / the / is", "Do / you / know / my / is", "Could / you / tell / my / was"],
        a: "Could / you / tell / the / is",
        tip: "Staff = Could you tell me. Where + subject + IS (present — live location). No inversion!"
      },
      {
        type: "mc",
        q: "Review: 'Could you tell me how much ___ to reserve a table for eight?'",
        options: ["it costs", "does it cost", "it cost"],
        a: "it costs",
        tip: "How much + subject (it) + verb (costs — no does!). Present — live pricing. Reserve a table = collocation."
      },
      {
        type: "mc",
        q: "Review: Which sentence has the correct word order?",
        options: [
          "Do you know whether the flight has been canceled?",
          "Do you know whether has the flight been canceled?",
          "Do you know whether the flight was canceled?"
        ],
        a: "Do you know whether the flight has been canceled?",
        tip: "Subject (the flight) before verb (has been). No inversion. Has been = Present Perfect — current status."
      },
      {
        type: "mc",
        q: "Review: Nadia at the desk (live): 'Could you tell me ___ my seat ___.' / Nadia reporting later: 'I asked ___ my seat ___.'",
        options: ["where / is / where / was", "where / was / where / is", "where / is / where / is"],
        a: "where / is / where / was",
        tip: "Live = IS (present). Reported later = WAS (backshift). Same structure — different time context!"
      },
      {
        type: "mc",
        q: "Review: 'Do you know ___ the delayed luggage ___ arrived on carousel 4 ___ carousel 6?'",
        options: ["whether / has / or", "if / has / or", "whether / is / or"],
        a: "whether / has / or",
        tip: "Alternatives = whether...or (never if...or!). Has arrived = Present Perfect — current status. Delayed = vocabulary."
      },
      {
        type: "mc",
        q: "Review: 'Could you tell me why my seat ___ been reassigned without my knowledge?'",
        options: ["hasn't", "isn't", "wasn't"],
        a: "hasn't",
        tip: "Why + subject + negative verb. Hasn't been reassigned = Present Perfect negative — current ongoing situation."
      },
      {
        type: "mc",
        q: "Review: Marcus to a stranger vs Marcus to a receptionist. Choose the correct pair:",
        options: [
          "Stranger: 'Do you know what time the flight departs?' / Receptionist: 'Could you tell me what time the flight departs?'",
          "Stranger: 'Could you tell me what time the flight departs?' / Receptionist: 'Do you know what time the flight departs?'",
          "Stranger: 'Do you know what time does the flight depart?' / Receptionist: 'Could you tell me what time does the flight depart?'"
        ],
        a: "Stranger: 'Do you know what time the flight departs?' / Receptionist: 'Could you tell me what time the flight departs?'",
        tip: "Do you know = stranger (may not know). Could you tell me = receptionist (should know). Same grammar inside — no does!"
      },
      {
        type: "mc",
        q: "Review: '___ ___ ___ me whether I ___ reserve a table for tonight or ___ book in advance?'",
        options: ["Could / you / tell / can / need to", "Do / you / know / could / need", "Could / you / tell / can / needed"],
        a: "Could / you / tell / can / need to",
        tip: "Could you tell me (staff). Whether...or (alternatives = whether!). Can = present ability. Need to = parallel structure. Reserve a table = collocation."
      },
      {
        type: "mc",
        q: "Review: What are the THREE rules for indirect questions?",
        options: [
          "No inversion + no auxiliary inside + no backshift (live question).",
          "No inversion + no auxiliary inside + always backshift.",
          "Always whether (not if) + no inversion + backshift."
        ],
        a: "No inversion + no auxiliary inside + no backshift (live question).",
        tip: "The three rules: subject before verb (no inversion), auxiliary disappears (no does/did), present tense stays (no backshift for live questions).",
        successTip: "🎉 UNIT 14 COMPLETE! Do you know + WH/if/whether ✅ Could you tell me + WH/if/whether ✅ No inversion ✅ No auxiliary ✅ No backshift (live) ✅ vs Reported speech (backshift) ✅ flight · delayed · canceled · luggage · seat · cost · reserve a table ✅ You ask — politely, precisely, and perfectly! ✈️🧳🍽️🏆"
      }
    ]
  }
};
